import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { toast } from "@loot-os/ui/components/sonner";
import { Switch } from "@loot-os/ui/components/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ErrorAlert } from "../../components/ErrorAlert";
import { SourcingRuleDialog, type SourcingRuleDraft } from "../../components/SourcingRuleDialog";
import { formatDate } from "../../lib/format";
import { useTRPC } from "../../lib/trpc";

const COLUMNS = ["Enabled", "Page", "Per run", "Tags", "Tenants", "Updated", ""] as const;

/** Which dialog is open: none, the add form, or the edit form for one rule. */
type Editing = { mode: "add" } | { mode: "edit"; id: string; rule: SourcingRuleDraft } | null;

/**
 * Vendor product sourcing: the sourcing rules, which StockX pages the daily sourcing cron reads for
 * products nobody has listed, how many it takes from each, and which tenants they are listed for.
 */
export function VendorProductSourcingPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const listOptions = trpc.platform.fulfillment.vendorProductSourcing.list.queryOptions();
  const rules = useQuery(listOptions);
  const [editing, setEditing] = useState<Editing>(null);
  // A new key per opening, so the form starts from the rule it opened on.
  const [opening, setOpening] = useState(0);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: listOptions.queryKey });

  const create = useMutation(
    trpc.platform.fulfillment.vendorProductSourcing.create.mutationOptions({
      onSuccess: () => {
        refresh();
        setEditing(null);
        toast.success("Sourcing rule added, disabled until you enable it.");
      },
    }),
  );
  const update = useMutation(
    trpc.platform.fulfillment.vendorProductSourcing.update.mutationOptions({
      onSuccess: ({ rule, copiedListings }) => {
        refresh();
        setEditing(null);
        toast.success(
          copiedListings > 0
            ? `Rule saved; ${copiedListings} listings copied to the added tenants.`
            : rule.isEnabled
              ? "Rule saved and enabled."
              : "Rule saved; disabled.",
        );
      },
    }),
  );

  const open = (next: Editing) => {
    setOpening((n) => n + 1);
    setEditing(next);
  };
  const save = (draft: SourcingRuleDraft) => {
    if (editing?.mode === "edit") {
      const { url: _url, ...patch } = draft;
      update.mutate({ id: editing.id, ...patch });
    } else {
      create.mutate(draft);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Vendor product sourcing</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Each day at 07:00 UTC the sourcing cron reads the page of every enabled sourcing rule
            below and lists the first products on it that are not listed yet. A rule with no tenant
            lists shared.
          </p>
        </div>
        <Button type="button" onClick={() => open({ mode: "add" })}>
          Add rule
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Pages</CardTitle>
          <CardDescription>{rules.data?.rules.length ?? 0} rules, in run order</CardDescription>
        </CardHeader>
        <CardContent>
          {rules.error && <ErrorAlert message={rules.error.message} />}
          {rules.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : (rules.data?.rules.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">
              No sourcing rules on this stage. Add one, or run the seed script in
              docs/fulfillment/vendor-product-sourcing.md.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {COLUMNS.map((label) => (
                      <TableHead key={label}>{label}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rules.data?.rules.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell>
                        <Switch
                          checked={rule.isEnabled}
                          disabled={update.isPending}
                          aria-label={rule.isEnabled ? "Disable rule" : "Enable rule"}
                          onCheckedChange={(isEnabled) => update.mutate({ id: rule.id, isEnabled })}
                        />
                      </TableCell>
                      <TableCell className="min-w-72 max-w-md whitespace-normal">
                        <a
                          href={rule.url}
                          target="_blank"
                          rel="noreferrer"
                          className="break-all font-mono text-xs underline-offset-2 hover:underline"
                        >
                          {rule.url}
                        </a>
                      </TableCell>
                      <TableCell>{rule.numNewProducts}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {rule.tags.map((tag) => (
                            <Badge key={tag} variant="secondary" className="font-mono">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {rule.tenantIds.length === 0 ? "shared" : rule.tenantIds.join(", ")}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {formatDate(rule.updatedAt)}
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            open({
                              mode: "edit",
                              id: rule.id,
                              rule: {
                                url: rule.url,
                                numNewProducts: rule.numNewProducts,
                                tags: rule.tags,
                                tenantIds: rule.tenantIds,
                              },
                            })
                          }
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <SourcingRuleDialog
        key={opening}
        open={editing !== null}
        rule={editing?.mode === "edit" ? editing.rule : undefined}
        isPending={create.isPending || update.isPending}
        onOpenChange={(next) => !next && setEditing(null)}
        onSave={save}
      />
    </div>
  );
}
